import Raven from 'raven-js'
import Vue from 'vue'
import axios from 'axios'

/**
 * Remap the locations and sort by title. Add a non value to index 0
 * @param data
 * @param config
 * @returns {Promise<*>}
 */
export const locationRemapSort = data => {
  return new Promise(resolve => {
    const locations = Object.keys(data).map(key => {
      const v = data[key]
      return {
        value: v.id,
        title: v.name,
        option_text: `${v.site_code} - ${v.name}`,
        site_code: v.site_code,
      }
    })

    locations.unshift({
      value: null,
      title: 'None',
      option_text: 'None',
    })

    resolve(locations)
  })
}

/**
 * Remap cross connect and sort by title
 * @param data
 * @param config
 * @returns {Promise<*>}
 */
export const crossConnectRemapSort = data => {
  return new Promise(resolve => {
    const crossConnects = Object.keys(data)
      .map(key => {
        const v = data[key]
        return {
          value: v.id,
          title: `${v.id} : ${v.name}`,
          option_text: `${v.id} - ${v.name}`,
        }
      })
      .sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1))

    crossConnects.unshift({
      value: null,
      title: 'None',
      option_text: 'None',
    })

    resolve(crossConnects)
  })
}

/**
 * Remap organisation and sort by title
 * @param data
 * @param config
 * @returns {Promise<*>}
 */
export const organisationRemapSort = data => {
  return new Promise(resolve => {
    // Store a standard array
    const organisations = Object.keys(data)
      .map(key => {
        const v = data[key]
        return {
          value: v.id,
          title: v.name,
          option_text: v.name,
        }
      })
      .sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1))

    // Adds a none option
    organisations.unshift({
      value: null,
      title: 'None',
      option_text: 'None',
    })

    resolve(organisations)
  })
}

/**
 * Element UI Notify Good
 * @param message
 * @param duration
 * @param position
 */
export const notifyGood = (message, duration = 4500, position = 'bottom-right') => {
  Vue.prototype.$notify({
    title: 'Success',
    message: message,
    type: 'success',
    position: position,
    duration: duration,
  })
}

/**
 * Element UI Notify Warn
 * @param message
 * @param duration
 * @param position
 */
export const notifyWarn = (message, duration = 4500, position = 'bottom-right') => {
  Vue.prototype.$notify({
    title: 'Warning',
    message: message,
    type: 'warning',
    position: position,
    duration: duration,
  })
}

/**
 * Element UI Notify bad
 * @param message
 * @param duration
 * @param position
 */
export const notifyBad = (message, duration = 4500, position = 'bottom-right') => {
  Vue.prototype.$notify({
    title: 'Error',
    message: message,
    type: 'error',
    position: position,
    duration: duration,
  })
}

/**
 * Log errors to Raven
 * @param error
 * @param callback
 * @returns {*}
 */
export const logError = (error, callback) => {
  if (!window.raven) return null

  // Reject ResizeObserver loop limit errors until handled
  // https://bugs.chromium.org/p/chromium/issues/detail?id=809574&q=resizeObserver&colspec=ID%20Pri%20M%20Stars%20ReleaseBlock%20Component%20Status%20Owner%20Summary%20OS%20Modified
  if (error.includes('ResizeObserver')) return null

  window.raven.captureException(`${error.status} - ${error.statusText} - ${error.data.message}`)
  error.eventId = Raven.lastEventId()
  localStorage.setItem('_error', JSON.stringify(error))

  if (callback) return callback(error)
}

/**
 * Handle fetch errors
 * @param res
 * @param callback
 * @returns {Promise<any> | Promise}
 */
export const handleErrors = (res, callback) => {
  return new Promise(reject => {
    switch (res.status) {
      case 401:
        notifyWarn('Your authentication token has timed out! Please log in')
        login()
        break
      case 404:
        notifyWarn('No results found')
        break
      default:
        logError(res, callback)
        break
    }
    reject(res.statusText)
  })
}

/**
 * Generate default options object
 * @param options
 * @returns {{cache: boolean, success: success, error: error}}
 */
export const generateFetchDefaults = options => {
  const defaults = {
    cache: true,
    success: () => {},
    error: err => {
      notifyBad(`Error - ${err.response.data.message}`)
      window.router.push('/error')
    },
  }

  Object.assign(defaults, options)
  return defaults
}

/**
 * Get stuff
 * @param uri
 * @param opt
 */
export const get = async (uri, opt) => {
  const options = generateFetchDefaults(opt)
  // const key = `${btoa(uri)}`;

  try {
    const res = await axios(uri, {
      method: 'GET',
      withCredentials: true,
    })
    options.success(res.data)
  } catch (err) {
    if (err.response) {
      return handleErrors(err.response)
    }
  }
}

/**
 * Post stuff
 * @param uri
 * @param opt
 */
export const post = async (uri, opt = {}) => {
  const options = {
    success: () => {},
    error: err => {
      const error = err.response?.data?.message
      notifyBad(error)
    },
    ...opt,
  }

  try {
    const res = await axios.post(uri, opt.data || null, {
      withCredentials: true,
    })
    options.success(res.data)
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        login()
      } else if (err.response.status === 400) {
        options.error(err.response)
      } else if (err.response.status === 500) {
        options.error(err.response)
      } else if (err.response.status === 502) {
        // TODO: this page doesn't even exist
        window.location = '/502.html'
      } else if (err.response.status === 404) {
        notifyWarn('No results found')
      } else {
        logError(err.response, options.error)
        notifyWarn(`${err.response.statusText}\r\r${err.response.data?.error}`, 0)
      }
    } else {
      logError(err, options.error)
    }
  }
}

/**
 * Request stuff
 * @param uri
 * @param opt
 */
export const request = async (uri, opt = {}) => {
  const options = {
    success: () => {},
    error: err => {
      const error = err.response?.data?.message
      notifyBad(error)
    },
    ...opt,
  }

  if (!options.method) {
    return
  }

  try {
    const res = await axios(uri, {
      method: options.method,
      data: opt.data || null,
      withCredentials: true,
    })
    options.success(res.data)
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        login()
        return
      } else if (err.response.status === 502) {
        // TODO: this page doesn't even exist
        window.location = '/502.html'
      } else if (err.response.status === 404) {
        notifyWarn('No results found')
      } else
        logError(err.response, options.error)
    } else {
      logError(err, options.error)
    }
  }
}

/**
 * Clear the cache
 */
export const clearCache = () => {
  localStorage.clear()
}

/**
 * Login
 */
export const login = () => {
  localStorage.setItem('_lastLocation', location.href)
  if (import.meta.env.MODE !== 'development') {
    window.location = '/auth/authenticate'
  }
}

/**
 * Something to do with bandwidth
 * @param rateMbps
 * @param short
 * @returns {string}
 */
export const bandwidthUnits = (rateMbps, short) => {
  if (!rateMbps) return 'null'

  const rate = parseFloat(rateMbps)

  if (rate > 1000) {
    if (short) return `${Math.floor(rate / 1000)} G`
    return `${(rate / 1000).toFixed(2)} Gbps`
  }

  if (short) return `${Math.floor(rate)} M`

  return `${rate} Mbps`
}

/**
 * Super duper handy debounce function
 * @param func
 * @param ms
 * @param immediate
 * @returns {Function}
 */
export const debounce = (func, ms, immediate) => {
  let timeout
  return function() {
    const context = this,
      args = arguments
    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, ms)
    if (callNow) func.apply(context, args)
  }
}

/**
 * Sort the object by the key 'name'
 * @param data
 * @returns {Promise<*>}
 */
export const sortByName = data => {
  return new Promise(resolve => {
    const sorted = data.sort((a, b) => {
      const aName = a.name.toUpperCase()
      const bName = b.name.toUpperCase()
      if (aName < bName) return -1
      if (aName > bName) return 1
      return 0
    })
    resolve(sorted)
  })
}

// function hasAuth(groups) {
//   let member = false;
//   groups.forEach(group => {
//     const currGroup = Forge._user.groups.find(g => {
//       return g.name === group;
//     });
//
//     if (currGroup.member === 1) member = true;
//   });
//
//   return member;
// }
